import { useCallback, useEffect, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import { t } from 'i18next';
import { IconButton } from '@components/buttons';
import { fonts } from '@theme/fontsCustomer';
import { DownloadIcon } from '@icons/DownloadIcon';
import { XIcon } from '@icons/XIcon';
import { LoadingIcon } from '@icons/loading/LoadingIcon';
import type { PdfFilePreview } from '@customHooks/useFileDialog';

// PDF.js worker 설정
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

type PDFPreviewModalProps = {
  pdfPreview: PdfFilePreview;
  updatePdfPreview: (pdfPreview: PdfFilePreview) => void;
  downloadFileFn?: (fileId: string) => void;
  enabledDownload?: boolean;
};
export const PDFPreviewModal = ({
  pdfPreview,
  updatePdfPreview,
  downloadFileFn,
  enabledDownload = false,
}: PDFPreviewModalProps) => {
  const { colors } = useTheme();
  const [numPages, setNumPages] = useState<number>(0);

  const handleClosePdfPreview = useCallback(() => {
    setNumPages(0);
    updatePdfPreview({
      open: false,
      fileId: null,
      file: null,
      fileName: '',
      numPages: 0,
    });
  }, [updatePdfPreview]);

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  // 모달이 열릴 때마다 numPages 리셋
  useEffect(() => {
    if (!pdfPreview.open) {
      setNumPages(0);
    }
  }, [pdfPreview.open]);

  // ESC 키 이벤트 처리
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && pdfPreview.open) {
        handleClosePdfPreview();
      }
    };

    if (pdfPreview.open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClosePdfPreview, pdfPreview.open]);

  return (
    <PdfPreviewModal open={pdfPreview.open} onClose={handleClosePdfPreview}>
      <PdfDialogTitle>
        <TitleSection>
          <DialogTitleText>{`${pdfPreview.fileName} ${t('File.Preview')}`}</DialogTitleText>
        </TitleSection>
        <IconButton variant="icon" onClick={handleClosePdfPreview}>
          <XIcon width={24} height={24} color={colors['ic-gray-main']} />
        </IconButton>
      </PdfDialogTitle>
      <PdfDialogContent>
        {pdfPreview.file && pdfPreview.open && (
          <>
            {enabledDownload && (
              <FloatingDownloadButton
                type="button"
                onClick={() => {
                  if (typeof downloadFileFn === 'function' && pdfPreview.fileId) {
                    downloadFileFn(pdfPreview.fileId);
                  }
                }}
              >
                <DownloadIcon width={16} height={16} />
              </FloatingDownloadButton>
            )}
            <PdfContainer>
              <Document
                key={pdfPreview.fileName}
                file={pdfPreview.file}
                onLoadSuccess={handleDocumentLoadSuccess}
                loading={
                  <LoadingWrapper>
                    <LoadingIcon />
                  </LoadingWrapper>
                }
              >
                {numPages > 0 &&
                  Array.from(new Array(numPages), (_, index) => (
                    <PageWrapper key={`page_${index + 1}`}>
                      <Page pageNumber={index + 1} width={554} scale={1.5} renderTextLayer renderAnnotationLayer />
                    </PageWrapper>
                  ))}
              </Document>
            </PdfContainer>
          </>
        )}
      </PdfDialogContent>
    </PdfPreviewModal>
  );
};

const PdfPreviewModal = styled(Dialog)`
  & .MuiDialog-paper {
    width: 850px;
    max-width: 850px;
    margin: auto;
  }
`;

const PdfDialogTitle = styled(DialogTitle)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 24px;
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
`;

const DialogTitleText = styled.div`
  ${fonts.Headline6}
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PdfDialogContent = styled(DialogContent)`
  position: relative;
  padding: 24px;
  overflow-y: auto;
  height: 80vh;
`;

const FloatingDownloadButton = styled.button`
  position: absolute;
  top: 16px;
  right: 44px;
  margin-left: auto;
  /* margin-right: 44px; */
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background-color: ${({ theme: { colors } }) => colors['bg-white']};
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10;
`;

const PdfContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 16px;
  overflow: hidden;
  background-color: ${({ theme: { colors } }) => colors['bg-white']};

  &:last-child {
    margin-bottom: 0;
  }
`;
